/**
 * AcceptInvitationScreen — Renders the public-link invitation accept flow.
 *
 * Moved out of `app/app/invite/[token].tsx` so the route file stays a thin
 * Expo Router boundary. Fetch + accept go through TanStack Query and the
 * project's authenticated apiClient (no more raw `Bearer ${userId}`).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useTheme } from '../../../src/theme/useTheme';
import { getInviteAcceptStyles } from '../../../appStyles/_inviteAccept.styles';
import { getJoinFlowGradientColors } from '../../../src/join/helpers/joinFlowScreen.helper';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics';
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper';
import {
  acceptInvitationByToken,
  fetchInvitationByToken,
} from '../../../src/invitations/helpers/invitationApi';
import { resolveInviterName } from '../../../src/invitations/helpers/resolveInviterName.helper';
import {
  ACCEPT_INVITATION_COPY,
  FRIENDS_QUERY_KEY,
  INVITATIONS_QUERY_KEY,
  TOKEN_PREVIEW_LENGTH,
  buildInvitationByTokenQueryKey,
} from './AcceptInvitationScreen.constants';

interface AcceptInvitationScreenProps {
  token: string | undefined;
}

const HOME_ROUTE = '/app/' as const;
const FRIENDS_ROUTE = '/app/friends' as const;

export function AcceptInvitationScreen({ token }: AcceptInvitationScreenProps) {
  const router = useRouter();
  const theme = useTheme();
  const styles = useMemo(() => getInviteAcceptStyles(theme), [theme]);
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const apiClient = useApiClient();
  const { showSnackbar } = useSnackbar();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const hasTrackedViewRef = useRef(false);
  const gradientColors = useMemo(() => getJoinFlowGradientColors(theme), [theme]);

  const invitationQuery = useQuery({
    queryKey: buildInvitationByTokenQueryKey(token),
    queryFn: () => {
      if (!token) {
        throw new Error(ACCEPT_INVITATION_COPY.ERROR_FALLBACK_TITLE);
      }
      return fetchInvitationByToken(token);
    },
    enabled: Boolean(token),
    retry: false,
  });

  const invitation = invitationQuery.data ?? null;
  const isLoading = invitationQuery.isLoading;
  const loadError = invitationQuery.isError
    ? invitationQuery.error instanceof Error
      ? invitationQuery.error.message
      : ACCEPT_INVITATION_COPY.ERROR_LOAD
    : null;

  useEffect(() => {
    if (!invitation || hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    track(ANALYTICS_EVENTS.INVITE_LINK_VIEWED, {
      token_preview: token?.substring(0, TOKEN_PREVIEW_LENGTH),
      has_account: isSignedIn,
    });
  }, [invitation, token, isSignedIn, track]);

  const acceptMutation = useMutation({
    mutationFn: () => {
      if (!token) {
        throw new Error(ACCEPT_INVITATION_COPY.ERROR_ACCEPT);
      }
      return acceptInvitationByToken(apiClient, token);
    },
    onSuccess: () => {
      track(ANALYTICS_EVENTS.FRIEND_ACCEPTED);
      void queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      showSnackbar(ACCEPT_INVITATION_COPY.ACCEPTED_SNACKBAR, 'success');
      router.push(FRIENDS_ROUTE);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : ACCEPT_INVITATION_COPY.ERROR_ACCEPT;
      showSnackbar(message, 'error');
    },
  });

  const handleAccept = useCallback(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      router.push(buildPublicSignInHref({ invite_token: token }) as `/${string}`);
      return;
    }
    acceptMutation.mutate();
  }, [acceptMutation, isLoaded, isSignedIn, router, token]);

  const handleCreateAccount = useCallback(() => {
    router.push(
      buildPublicSignInHref({ signup: true, invite_token: token }) as `/${string}`,
    );
  }, [router, token]);

  const handleGoHome = useCallback(() => {
    router.push(HOME_ROUTE);
  }, [router]);

  if (isLoading) {
    return (
      <LinearGradient colors={gradientColors} style={styles.loadingGradient}>
        <ActivityIndicator size="large" color={theme.primary} />
        <Text style={styles.loadingText}>{ACCEPT_INVITATION_COPY.LOADING}</Text>
      </LinearGradient>
    );
  }

  if (loadError || !invitation) {
    return (
      <LinearGradient colors={gradientColors} style={styles.errorGradient}>
        <Text style={styles.errorTitle}>
          {loadError ?? ACCEPT_INVITATION_COPY.ERROR_FALLBACK_TITLE}
        </Text>
        <Text style={styles.errorMessage}>{ACCEPT_INVITATION_COPY.ERROR_BODY}</Text>
        <Pressable
          onPress={handleGoHome}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>{ACCEPT_INVITATION_COPY.GO_HOME}</Text>
        </Pressable>
      </LinearGradient>
    );
  }

  const inviterName = resolveInviterName(invitation, ACCEPT_INVITATION_COPY.GENERIC_INVITER);
  const accepting = acceptMutation.isPending;

  return (
    <LinearGradient colors={gradientColors} style={styles.gradient}>
      <View style={styles.inner}>
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <Text style={styles.iconText}>{ACCEPT_INVITATION_COPY.WAVE_EMOJI}</Text>
          </View>

          <Text style={styles.title}>{ACCEPT_INVITATION_COPY.TITLE}</Text>

          <Text style={styles.subtitle}>
            <Text style={styles.subtitleBold}>{inviterName}</Text>
            {ACCEPT_INVITATION_COPY.SUBTITLE_SUFFIX}
          </Text>

          <Text style={styles.description}>{ACCEPT_INVITATION_COPY.DESCRIPTION}</Text>

          {isSignedIn ? (
            <Pressable
              onPress={handleAccept}
              disabled={accepting}
              style={({ pressed, hovered }) => [
                styles.primaryButton,
                accepting && styles.primaryButtonDisabled,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                !accepting && pressed && styles.primaryButtonPressed,
                !accepting && hovered && Platform.OS === 'web' && styles.primaryButtonHover,
              ]}
            >
              {accepting ? (
                <ActivityIndicator color={theme.onPrimary} />
              ) : (
                <Text style={styles.primaryButtonText}>
                  {ACCEPT_INVITATION_COPY.ACCEPT_BUTTON}
                </Text>
              )}
            </Pressable>
          ) : (
            <>
              <Pressable
                onPress={handleAccept}
                style={({ pressed, hovered }) => [
                  styles.primaryButton,
                  styles.buttonSpacing,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.primaryButtonPressed,
                  hovered && Platform.OS === 'web' && styles.primaryButtonHover,
                ]}
              >
                <Text style={styles.primaryButtonText}>
                  {ACCEPT_INVITATION_COPY.SIGN_IN_TO_ACCEPT}
                </Text>
              </Pressable>
              <Pressable
                onPress={handleCreateAccount}
                style={({ pressed, hovered }) => [
                  styles.secondaryButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.secondaryButtonPressed,
                  hovered && Platform.OS === 'web' && styles.secondaryButtonHover,
                ]}
              >
                <Text style={styles.secondaryButtonText}>
                  {ACCEPT_INVITATION_COPY.CREATE_ACCOUNT}
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </View>
    </LinearGradient>
  );
}
